import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LabelList,
    Legend,
    Cell,
} from 'recharts';
import { CHART_DIMENSIONS, TEXT_STYLES } from './styles';

const CHART_COLORS = [
    '#4A90E2', // Mėlyna - geras kontrastas, tinka daltonizmui
    '#8B7DA9', // Levandų - neutrali, rami spalva
    '#9B6B9E', // Švelniai violetinė - gerai matoma
    '#F4A261', // Šviesiai oranžinė - šilta, bet ne per ryški
    '#45B7D1', // Turkio - šviesi ir aiški
    '#B784A7', // Šviesiai rožinė - švelni, bet kontrastinga
];

const renderYAxisTick = ({ x, y, payload }) => {
    const lines = payload.value.split(/(?<=\S{40}\S+)\s+/g);
    const lineHeight = CHART_DIMENSIONS.ITEM.LINE_HEIGHT;
    const totalHeight = (lines.length - 1) * lineHeight;
    const startY = y - (totalHeight / 2);

    return (
        <g>
            {lines.map((line, index) => (
                <text
                    key={index}
                    x={x}
                    y={startY + (index * lineHeight)}
                    textAnchor="end"
                    dominantBaseline="middle"
                    style={{
                        ...TEXT_STYLES.AXIS_TICK,
                        fill: 'currentColor',
                    }}
                >
                    {line}
                </text>
            ))}
        </g>
    );
};



const renderCustomBarLabel = (props) => {
    const { x, y, width, height, value } = props;
    return (
        <text
            x={x + width + 5}
            y={y + height / 2}
            textAnchor="start"
            dominantBaseline="central"
            style={{
                fontSize: '18px',
                fontWeight: 500,
                fill: '#000',
            }}
        >
            {`${Number.isInteger(value) ? value : value?.toFixed(1)}%`}
        </text>
    );
};

export const BarChartComponent = ({ data, CustomTooltip, renderYAxisTick, yAxisWidth, theme }) => {
    // Calculate dynamic height based on number of items and text length
    const itemHeight = CHART_DIMENSIONS.ITEM.BASE_HEIGHT;
    const maxLines = Math.max(...data.map(item => 
        Math.ceil(item.text.length / 40)
    ));
    const adjustedItemHeight = itemHeight + (maxLines - 1) * CHART_DIMENSIONS.BAR.SIZE_INCREMENT;
    const chartHeight = Math.max(
        CHART_DIMENSIONS.BAR.MIN_HEIGHT,
        data.length * adjustedItemHeight + CHART_DIMENSIONS.PADDING.CHART_BOTTOM
    );

    return (
        <ResponsiveContainer width="100%" height={chartHeight} minWidth={300}>
            <BarChart
                data={data}
                layout="vertical"
                margin={{
                    top: 20,
                    right: 0,
                    left: 80,
                    bottom: 20,
                }}
                barSize={45}
            >
                <CartesianGrid 
                    stroke="#e0e0e0"
                    horizontal={true}
                    verticalPoints={[]}
                    opacity={0.5}
                />
                <XAxis 
                    type="number" 
                    domain={[0, 100]} 
                    allowDataOverflow={true}
                    tickFormatter={(value) => `${Number.isInteger(value) ? value : value.toFixed(1)}%`}
                    ticks={[0, 25, 50, 75, 100]}
                    tick={{
                        fontSize: 12,
                        fontWeight: 'bold',
                        fill: '#666',
                        fontSize: '12px'
                    }}
                    axisLine={{ stroke: '#E0E0E0' }}
                    tickLine={{ stroke: '#E0E0E0' }}
                />
                <YAxis
                    type="category"
                    dataKey="text"
                    width={yAxisWidth}
                    tick={renderYAxisTick}
                    interval={0}
                    axisLine={{ stroke: '#E0E0E0' }}
                    orientation="left"
                />
                <Tooltip 
                    content={<CustomTooltip />} 
                    cursor={{ fill: 'rgba(0,0,0,0.05)' }}
                />
                <Bar
                    name="Atsakymai"
                    dataKey="value"
                    minPointSize={20}
                    radius={[0, 4, 4, 0]}
                >
                    {data.map((entry, index) => (
                        <Cell 
                            key={`cell-${index}`} 
                            fill={CHART_COLORS[index % CHART_COLORS.length]} 
                            fillOpacity={entry.value === 0 ? 0.3 : 1}
                        />
                    ))}
                    <LabelList
                        content={(props) => renderCustomBarLabel({ ...props, theme })}
                        position="right"
                    />
                </Bar>

            </BarChart>
        </ResponsiveContainer>
    );
};
